import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, X } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@reflex/ui/components/ui/command';
import { OwnerChip } from '@reflex/ui/components/ownership/OwnerChip';
import { useResolvedOwner } from '@reflex/ui/components/ownership/useResolvedOwner';
import { useOrgRoutes } from '@reflex/ui/client/context';
import { rankPersonas } from './persona-search.js';
import { PersonaListIcon } from './PersonaListIcon.js';
import type { AgentPersona } from '../shared/types.js';

export interface PersonaCommandListProps {
  /** Personas pulled from the React Query cache (already org-scoped). */
  personas: AgentPersona[];
  /**
   * Called with the picked persona. The host component is responsible
   * for closing the surrounding dialog/popover.
   */
  onSelect: (persona: AgentPersona) => void;
  /**
   * When provided, a "No persona" option is rendered above the list and
   * invokes this callback when chosen. Used by the launch dialog header
   * so the user can clear an active persona.
   */
  onClear?: () => void;
  /**
   * When provided, the empty-state CTA opens a create dialog inline
   * instead of navigating away. Used by the launch dialog to avoid
   * tearing down the in-progress launch.
   */
  onCreate?: () => void;
  /** Suggested maximum number of results. Defaults to 20. */
  limit?: number;
  /** Initial query string. Defaults to empty (full ordered list). */
  initialQuery?: string;
  /** Controls whether the search input autofocuses. Defaults to true. */
  autoFocus?: boolean;
  /**
   * Maximum height applied to the result list. Defaults to a value
   * appropriate for popovers; pass a larger value when embedding in a
   * dialog.
   */
  maxListHeightClassName?: string;
}

/**
 * Search-driven persona picker that re-uses the same `rankPersonas`
 * scorer and `OwnerChip` indicator that the rest of the app uses.
 * Embedded by both `PersonaPickerDialog` (the full-screen dialog
 * variant) and `LaunchPersonaHeader` (the inline popover variant), so
 * results stay visually consistent across surfaces. Each row renders
 * the persona's configured icon with a hover preview for metadata.
 */
export function PersonaCommandList({
  personas,
  onSelect,
  onClear,
  onCreate,
  limit = 20,
  initialQuery = '',
  autoFocus = true,
  maxListHeightClassName = 'max-h-[280px]',
}: PersonaCommandListProps) {
  const [query, setQuery] = useState(initialQuery);
  // Bare `/configure/...` is not a legacy root redirect — the compat catch-all
  // treats `configure` as an org slug and lands on `/orgs/configure/...` (404).
  // Org-scope this CTA the same way the rest of the persona surfaces do.
  const { agentPersonas } = useOrgRoutes();

  const matches = useMemo(() => rankPersonas(personas, query, limit), [personas, query, limit]);

  if (personas.length === 0) {
    return (
      <div
        className="flex flex-col items-center gap-2 px-4 py-6 text-center text-xs text-muted-foreground"
        data-testid="persona-command-list-empty"
      >
        <p>You haven&apos;t created any personas yet.</p>
        {onCreate ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="inline-flex items-center gap-1.5 font-medium text-foreground hover:underline"
            onClick={onCreate}
          >
            <UserPlus className="h-3.5 w-3.5" />
            Create a persona
          </Button>
        ) : (
          <Link
            to={agentPersonas}
            className="inline-flex items-center gap-1.5 font-medium text-foreground hover:underline"
          >
            <UserPlus className="h-3.5 w-3.5" />
            Create a persona
          </Link>
        )}
      </div>
    );
  }

  return (
    <Command shouldFilter={false}>
      <CommandInput
        placeholder="Search personas..."
        value={query}
        onValueChange={setQuery}
        autoFocus={autoFocus}
      />
      <CommandList className={maxListHeightClassName}>
        <CommandEmpty>No personas match.</CommandEmpty>
        {onClear && (
          <>
            <CommandGroup>
              <CommandItem value="__none__" onSelect={onClear}>
                <X className="h-4 w-4" />
                <span>No persona</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        <CommandGroup heading="Personas">
          {matches.map((persona) => (
            <PersonaCommandItem key={persona.id} persona={persona} onSelect={onSelect} />
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

interface PersonaCommandItemProps {
  persona: AgentPersona;
  onSelect: (persona: AgentPersona) => void;
}

/**
 * Single persona row inside `PersonaCommandList`. Pulled into its own
 * component so each row can call `useResolvedOwner` independently — the
 * hook reads from cached query data, but Rules of Hooks require it to
 * sit inside a component, not the parent's `.map(...)` callback. The
 * resolved owner name feeds the search keywords; the `OwnerChip`
 * resolves the preview target on its own.
 */
function PersonaCommandItem({ persona, onSelect }: PersonaCommandItemProps) {
  const resolvedOwner = useResolvedOwner({ ownerId: persona.ownerId });

  const keywords = [
    persona.agentType,
    persona.model ?? '',
    persona.description ?? '',
    resolvedOwner.ownerName ?? '',
  ].filter(Boolean);

  return (
    <CommandItem
      value={`persona:${persona.id}`}
      keywords={keywords}
      onSelect={() => onSelect(persona)}
    >
      <PersonaListIcon persona={persona} />
      <span className="truncate">{persona.name}</span>
      <span className="ml-2 text-xs text-muted-foreground">{persona.agentType}</span>
      <span className="ml-auto">
        <OwnerChip ownerId={persona.ownerId} />
      </span>
    </CommandItem>
  );
}
